import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Mail, MapPin, ArrowRight, Scan, Anchor, Loader2, Search } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ENDPOINTS } from '../api/config';

export default function HomePage() {
  const [donating, setDonating] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  const handleDonate = async (packageId, amount = null) => {
    try {
      setDonating(true);
      const originUrl = window.location.origin;
      
      const response = await axios.post(ENDPOINTS.DONATE_CHECKOUT, {
        package_id: packageId,
        custom_amount: amount,
        origin_url: originUrl
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Failed to create checkout:", error);
      toast.error("Failed to start donation. Please try again.");
      setDonating(false);
    }
  };

  const handleCustomDonate = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error("Please enter an amount of at least £1");
      return;
    }
    handleDonate("custom", amount);
  };

  return (
    <main className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-warning relative" data-testid="hero-section">
        <div className="relative z-10 max-w-5xl mx-auto px-4">
          {/* Main Warning Box */}
          <div className="warning-box text-center mb-32" data-testid="warning-box">
            <h1 className="font-display text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-bold uppercase tracking-tighter leading-none text-white mb-4">
              CONSUMERISM
              <br />
              <span className="text-red-600">KILLS</span>
            </h1>
            <div className="w-full h-1 bg-white my-4 sm:my-6" />
            <p className="font-display text-xl sm:text-3xl md:text-4xl font-bold uppercase tracking-wider sm:tracking-widest text-white">
              QUIT NOW
            </p>
          </div>

          {/* Two Pathway CTAs */}
          <div className="flex flex-col items-center gap-4 mb-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              <Link to="/scan" data-testid="scan-cta-button">
                <Button 
                  className="w-full bg-white text-black font-bold uppercase tracking-widest px-4 py-5 text-xs sm:text-sm hover:bg-red-600 hover:text-white transition-colors duration-150 rounded-none border-2 border-white btn-brutal"
                >
                  <Scan className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>SCAN BARCODE</span>
                </Button>
              </Link>
              <Link to="/search" data-testid="search-cta-button">
                <Button 
                  className="w-full bg-red-600 text-white font-bold uppercase tracking-widest px-4 py-5 text-xs sm:text-sm hover:bg-red-700 transition-colors duration-150 rounded-none border-2 border-red-600 btn-brutal"
                >
                  <Search className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>SEARCH & GENERATE</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Intro Text */}
          <div className="text-center mb-20">
            <p className="font-mono text-gray-400 max-w-xl mx-auto">
              Every product carries invisible debt, to people, ecosystems, displaced communities and to future generations. Investigate to reveal the true cost; scan or search and generate a report to see the documented impacts.
            </p>
          </div>

          {/* Cessation Support Card */}
          <div className="cessation-card max-w-2xl mx-auto" data-testid="cessation-card">
            <h3 className="font-display text-2xl font-bold uppercase tracking-widest text-white">
              CONSUMERISM CESSATION SUPPORT
            </h3>
            
            <div className="space-y-4 font-mono text-sm">
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <span className="text-gray-500 uppercase text-xs tracking-widest block">Phone</span>
                  <span className="text-white">+44 800 QUIT NOW</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <span className="text-gray-500 uppercase text-xs tracking-widest block">Email</span>
                  <span className="text-white">quit@factual-labelling.org</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <MapPin className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <span className="text-gray-500 uppercase text-xs tracking-widest block">Post</span>
                  <span className="text-white">
                    FLI Cessation Services<br />
                    123 Truth Street<br />
                    London, EC1A 1AA
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-800">
              <p className="font-mono text-gray-400 text-sm italic">
                &quot;Every purchase is a vote.<br />
                Abstaning is also a vote. The right thing to do is to not buy the thing.&quot;
              </p>
            </div>
          </div>

          {/* Donate Section */}
          <div className="max-w-2xl mx-auto mt-16" data-testid="donate-section">
            <div className="border-t border-dashed border-gray-700 pt-12">
              <div className="flex items-center gap-3 mb-6">
                <Anchor className="w-5 h-5 text-gray-500" />
                <h3 className="font-display text-xl font-bold uppercase tracking-widest text-gray-400">
                  KEEP THE SHIP SAILING
                </h3>
              </div>
              
              <p className="font-mono text-sm text-gray-500 mb-6 leading-relaxed">
                If you&apos;ve found this app useful and have funds to spare, send us over a few quid.
              </p>
              
              <p className="font-mono text-sm text-gray-400 mb-8">
                We only need to cover our costs.<br />
                <span className="text-white">Everything else goes to ecosystem regeneration.</span>
              </p>

              {/* Donate Amounts */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button 
                  onClick={() => handleDonate("small")}
                  disabled={donating}
                  className="font-mono text-sm px-6 py-3 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="donate-3"
                >
                  {donating ? <Loader2 className="w-4 h-4 animate-spin" /> : "£3"}
                </button>
                <button 
                  onClick={() => handleDonate("medium")}
                  disabled={donating}
                  className="font-mono text-sm px-6 py-3 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="donate-5"
                >
                  {donating ? <Loader2 className="w-4 h-4 animate-spin" /> : "£5"}
                </button>
                <button 
                  onClick={() => handleDonate("large")}
                  disabled={donating}
                  className="font-mono text-sm px-6 py-3 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="donate-10"
                >
                  {donating ? <Loader2 className="w-4 h-4 animate-spin" /> : "£10"}
                </button>
                <button 
                  onClick={() => setShowCustom(!showCustom)}
                  disabled={donating}
                  className="font-mono text-sm px-6 py-3 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="donate-custom"
                >
                  Custom
                </button>
              </div>

              {/* Custom Amount Input */}
              {showCustom && (
                <div className="flex gap-3 mb-6">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="font-mono text-gray-500">£</span>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="bg-black border-gray-700 text-white font-mono rounded-none focus:border-gray-500"
                      data-testid="custom-amount-input"
                    />
                  </div>
                  <button
                    onClick={handleCustomDonate}
                    disabled={donating || !customAmount}
                    className="font-mono text-sm px-6 py-3 bg-white text-black hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="custom-donate-btn"
                  >
                    {donating ? <Loader2 className="w-4 h-4 animate-spin" /> : "DONATE"}
                  </button>
                </div>
              )}

              <p className="font-mono text-xs text-gray-600">
                No targets. No tiers. No rewards.<br />
                Just a contribution if you can.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-24 px-4 border-t border-gray-800" data-testid="info-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-800">
            <div className="p-8 border-b md:border-b-0 md:border-r border-gray-800">
              <span className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-4">01</span>
              <h3 className="font-display text-xl font-bold uppercase text-white mb-3">INVESTIGATE</h3>
              <p className="font-mono text-sm text-gray-400">
                Scan a barcode, enter a product name, or browse pre-generated reports in the repository. FLI queries multiple databases to identify it and create reports of varying depth. This isn't product-level supply chain tracing as that's impossible for most consumer goods due to corporate opacity, supplier fragmentation, and deliberate obscurity in global logistics. Instead, we treat the individual product as an entry point into its category's documented impact patterns. The barcode gets you to the research. The research tells you what the category costs.
              </p>
            </div>
            
            <div className="p-8 border-b md:border-b-0 md:border-r border-gray-800">
              <span className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-4">02</span>
              <h3 className="font-display text-xl font-bold uppercase text-white mb-3">ANALYZE</h3>
              <p className="font-mono text-sm text-gray-400">
                Six parallel research queries synthesize peer-reviewed literature, human rights documentation, environmental monitoring data, labor statistics, corporate filings, and investigative journalism across seven impact domains: energy, minerals, labor, water, wildlife, ocean, deforestation.
This is category-level synthesis, not product-level tracking. We document what is known about how a category is produced—the energy intensity of semiconductor fabrication, the forced labor patterns in palm oil supply chains, the water depletion from cotton cultivation. The research identifies governance failures, regulatory gaps, and systemic externalities that persist regardless of which specific brand you're holding.
Sources are marked by type and confidence. Academic literature receives highest weight. NGO investigative reports and monitoring systems provide documented cases where academic coverage is thin. Corporate sustainability claims are treated as marketing unless independently verified.
              </p>
            </div>
            
            <div className="p-8">
              <span className="font-mono text-xs uppercase tracking-widest text-gray-500 block mb-4">03</span>
              <h3 className="font-display text-xl font-bold uppercase text-white mb-3">REVEAL</h3>
              <p className="font-mono text-sm text-gray-400">
                You receive a structured impact report across seven domains, with citations for every claim. Reports are tiered by research depth (Quick / Standard / Deep), but all share the same constraints:

No scores. Impact cannot be reduced to numbers that permit comparison shopping.
No alternatives. We do not tell you what to buy instead.
No redemption. There is no consumption choice that absolves you of systemic complicity.

What you get instead: clinical documentation of energy inputs, mineral extraction conditions, labor arrangements, water consumption, wildlife displacement, ocean pollution, and deforestation drivers. Cited sources with DOI links where available. Confidence levels for each claim. Explicit notation of conflicts in the literature and gaps in the research.
The report does not make you comfortable. It does not guide you toward better purchasing. It shows you what the ledger looks like when opacity is reduced, even partially, even temporarily. What you do with that discomfort is not our concern. 
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disavowal Section - v1.3 Compliance */}
      <section className="py-12 px-4 border-t border-gray-800 bg-red-950/20" data-testid="disavowal-section">
        <div className="max-w-3xl mx-auto">
          <div className="border border-red-800/50 p-6 bg-black/50">
            <p className="font-mono text-sm text-red-400 leading-relaxed">
              <strong className="uppercase tracking-widest block mb-3">DISAVOWAL</strong>
              FLI cannot make your consumption ethical. No app can. The impacts documented here result from systems that individual choices cannot change. This app provides visibility, not absolution.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
